import { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import type {
	FwQueryFilter,
	FwQueryState,
} from "@/components/fw/data-table/useFwQuery";
import { OrdersControl } from "@/components/Orders/OrdersControl";
import { useTranslate } from "@/hooks/useTranslate/useTranslate";
import type {
	FwStandardModelsFwQueryResponseStorefrontOrderDto,
	StorefrontOrderDto,
} from "@/lib/storefront-api";
import { SiteLayout } from "../components/SiteLayout";
import { useSiteLayoutState } from "../hooks/useSiteLayoutState";
import { useStorefrontSettingsState } from "../hooks/useStorefrontSettingsState";
import { sdkAxiosInstance } from "../storefrontsdk";

const DEFAULT_PAGE_NO = 1;
const DEFAULT_PAGE_SIZE = 20;

type OrderRow = StorefrontOrderDto;

interface OrdersPageBrowserState {
	pageNo: number;
	pageSize: number;
	sort: string;
	filters: FwQueryFilter<OrderRow>[];
}

const parsePositiveInt = (value: string | null, fallback: number) => {
	const parsed = Number.parseInt(value ?? "", 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const parseFilters = (value: string | null): FwQueryFilter<OrderRow>[] => {
	if (!value) {
		return [];
	}

	try {
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? (parsed as FwQueryFilter<OrderRow>[]) : [];
	} catch {
		return [];
	}
};

function useOrdersPageState() {
	const [searchParams, setSearchParams] = useSearchParams();

	const state = useMemo<OrdersPageBrowserState>(
		() => ({
			pageNo: parsePositiveInt(searchParams.get("pageno"), DEFAULT_PAGE_NO),
			pageSize: parsePositiveInt(
				searchParams.get("pagesize"),
				DEFAULT_PAGE_SIZE,
			),
			sort: searchParams.get("sort") ?? "",
			filters: parseFilters(searchParams.get("filters")),
		}),
		[searchParams],
	);

	const updateState = useCallback(
		(nextState: Partial<OrdersPageBrowserState>) => {
			const mergedState: OrdersPageBrowserState = {
				...state,
				...nextState,
			};
			const nextSearchParams = new URLSearchParams(searchParams);

			if (mergedState.pageNo > DEFAULT_PAGE_NO) {
				nextSearchParams.set("pageno", String(mergedState.pageNo));
			} else {
				nextSearchParams.delete("pageno");
			}

			if (mergedState.pageSize !== DEFAULT_PAGE_SIZE) {
				nextSearchParams.set("pagesize", String(mergedState.pageSize));
			} else {
				nextSearchParams.delete("pagesize");
			}

			if (mergedState.sort) {
				nextSearchParams.set("sort", mergedState.sort);
			} else {
				nextSearchParams.delete("sort");
			}

			if (mergedState.filters.length > 0) {
				nextSearchParams.set("filters", JSON.stringify(mergedState.filters));
			} else {
				nextSearchParams.delete("filters");
			}

			setSearchParams(nextSearchParams, { replace: true });
		},
		[searchParams, setSearchParams, state],
	);

	return { state, updateState };
}

function buildOrdersRequestUrl(browserState: OrdersPageBrowserState): string {
	const params = new URLSearchParams();
	params.set("pageno", String(browserState.pageNo));
	params.set("pagesize", String(browserState.pageSize));

	if (browserState.sort) {
		params.set("sort", browserState.sort);
	}

	if (browserState.filters.length > 0) {
		params.set(
			"filter",
			JSON.stringify(
				browserState.filters.map((filter) => ({
					field: String(filter.field),
					op: filter.op,
					value: filter.value,
				})),
			),
		);
	}

	return `/api/v1/storefront/orders?${params.toString()}`;
}

function useOrdersQuery(
	browserState: OrdersPageBrowserState,
	updateState: (nextState: Partial<OrdersPageBrowserState>) => void,
): FwQueryState<OrderRow> {
	const location = useLocation();
	const siteLayoutState = useSiteLayoutState();
	const [items, setItems] = useState<OrderRow[]>([]);
	const [totalItems, setTotalItems] = useState(0);
	const [errorMessage, setErrorMessage] = useState("");
	const [isPending, setIsPending] = useState(false);
	const redirectTarget = `${location.pathname}${location.search}${location.hash}`;

	const totalPages =
		totalItems > 0 ? Math.ceil(totalItems / browserState.pageSize) : 0;
	const currentUrl = useMemo(
		() => buildOrdersRequestUrl(browserState),
		[
			browserState,
			browserState.filters,
			browserState.pageNo,
			browserState.pageSize,
			browserState.sort,
		],
	);

	const loadOrders = useCallback(async () => {
		setIsPending(true);
		setErrorMessage("");

		try {
			const response =
				await sdkAxiosInstance.get<FwStandardModelsFwQueryResponseStorefrontOrderDto>(
					buildOrdersRequestUrl(browserState),
				);
			const responseData = response.data;
			setItems(responseData.Items ?? []);
			setTotalItems(responseData.TotalItems ?? 0);
		} catch (error) {
			if (error instanceof AxiosError) {
				if (error.response?.status === 401 || error.response?.status === 403) {
					siteLayoutState.redirectToLogin(redirectTarget);
					return;
				}
				setErrorMessage(
					error.response?.data?.message ||
						error.message ||
						"Unable to load orders.",
				);
			} else if (error instanceof Error) {
				setErrorMessage(error.message);
			} else {
				setErrorMessage("Unable to load orders.");
			}
		} finally {
			setIsPending(false);
		}
	}, [
		browserState,
		browserState.filters,
		browserState.pageNo,
		browserState.pageSize,
		browserState.sort,
		redirectTarget,
		siteLayoutState,
	]);

	useEffect(() => {
		void loadOrders();
	}, [loadOrders]);

	return {
		pageNo: browserState.pageNo,
		pageSize: browserState.pageSize,
		filters: browserState.filters,
		sort: browserState.sort,
		isPending,
		isFetching: isPending,
		errorMessage,
		items,
		totalItems,
		totalPages,
		url: currentUrl,
		firstBtnDisabled: isPending || browserState.pageNo <= 1 || totalPages <= 1,
		prevBtnDisabled: isPending || browserState.pageNo <= 1,
		nextBtnDisabled:
			isPending || totalPages === 0 || browserState.pageNo >= totalPages,
		lastBtnDisabled:
			isPending || totalPages === 0 || browserState.pageNo >= totalPages,
		pageNoBtnDisabled: isPending || totalPages === 0,
		pageSizeBtnDisabled: isPending,
		reset: () => {
			updateState({
				pageNo: DEFAULT_PAGE_NO,
				pageSize: DEFAULT_PAGE_SIZE,
				sort: "",
				filters: [],
			});
		},
		loadAsync: loadOrders,
		firstPageAsync: async () => {
			updateState({ pageNo: 1 });
		},
		prevPageAsync: async () => {
			updateState({ pageNo: Math.max(1, browserState.pageNo - 1) });
		},
		nextPageAsync: async () => {
			updateState({
				pageNo:
					totalPages > 0
						? Math.min(totalPages, browserState.pageNo + 1)
						: browserState.pageNo,
			});
		},
		lastPageAsync: async () => {
			updateState({
				pageNo: totalPages > 0 ? totalPages : browserState.pageNo,
			});
		},
		setPageNoAsync: async (pageNo: number) => {
			if (!Number.isFinite(pageNo) || pageNo < 1) {
				return;
			}

			if (totalPages > 0 && pageNo > totalPages) {
				updateState({ pageNo: totalPages });
				return;
			}

			updateState({ pageNo });
		},
		setPageSizeAsync: async (pageSize: number) => {
			if (!Number.isFinite(pageSize) || pageSize < 1) {
				return;
			}

			updateState({ pageNo: 1, pageSize });
		},
		setFiltersAsync: async (filters: FwQueryFilter<OrderRow>[]) => {
			updateState({ pageNo: 1, filters });
		},
		setSortAsync: async (sort: string) => {
			updateState({ sort });
		},
	};
}

export const OrdersPage: React.FC = () => {
	const translate = useTranslate();
	const storefrontSettingsState = useStorefrontSettingsState();
	const { state, updateState } = useOrdersPageState();
	const ordersQuery = useOrdersQuery(state, updateState);
	const didInitRef = useRef(false);

	useEffect(() => {
		if (didInitRef.current) {
			return;
		}
		didInitRef.current = true;
		storefrontSettingsState.loadAsync().then(() => {
			ordersQuery.setSortAsync("OrderDate desc");
		});
	}, [storefrontSettingsState, ordersQuery]);

	return (
		<SiteLayout
			title={translate("Quote & Orders")}
			mainChildren={
				<div className="w-full max-w-[1420px] mx-auto">
					<OrdersControl query={ordersQuery} />
				</div>
			}
		/>
	);
};
